import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { AlertCircle, Check, ChevronLeft, ChevronRight, Clock, Coins, Copy, History, PiggyBank, RefreshCw, Search, Sparkles, Store as StoreIcon } from "lucide-react";
import useDocumentTitle from "../../hooks/useDocumentTitle";
import useGoerOneAuth from "../../hooks/useGoerOneAuth";
import WebHeader from "./WebHeader";
import { formatINR } from "./VoucherCheckoutPanel";
import { fetchLoyaltyRedemption } from "../../store/loyaltySlices/loyaltyRedemptionSlice";
const CONFIRMATION_PATH = "/web/payment-confirmation";
const PER_PAGE = 25;
const num = (v) => Number(v) || 0;

// "2026-10-05 11:38:39" -> Date
const parseDate = (value) => {
    if (!value) return null;
    const d = new Date(String(value).replace(" ", "T"));
    return Number.isNaN(d.getTime()) ? null : d;
};

const formatDateTime = (value) => {
    const d = parseDate(value);
    return d
        ? `${d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}, ${d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`
        : "";
};

const STATUS_STYLES = {
    completed: "bg-emerald-100 text-emerald-700",
    pending: "bg-amber-100 text-amber-700",
    cancelled: "bg-rose-100 text-rose-700",
    failed: "bg-rose-100 text-rose-700",
};

// cashback_status: "false" = pending, "true" = approved
const isCashbackApproved = (r) =>
    ["true", "1"].includes(String(r.cashbackstatus).toLowerCase());

function CopyRef({ text }) {
    const [copied, setCopied] = useState(false);
    const onCopy = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            /* clipboard not available */
        }
    };
    return (
        <button
            type="button"
            onClick={onCopy}
            className="inline-flex cursor-pointer items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 font-mono text-[11px] font-semibold tracking-wider text-slate-600 transition hover:bg-slate-200"
            title="Copy reference number"
        >
            {text}
            {copied ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />}
        </button>
    );
}

function HistoryCard({ r }) {
    const navigate = useNavigate();
    const saved = num(r.discountCust); // customer's discount = the main discount
    const coins = num(r.CashBack);
    const approved = isCashbackApproved(r);
    const status = String(r.status || "").toLowerCase();

    const referenceNumber = r.referneceNo;
        const confirmationState = {
            referenceNumber,
            storeName: r.store_name,
            storeId: r?.stores_id,
        };

    return (
        <div onClick={() =>navigate(`${CONFIRMATION_PATH}?ref=${encodeURIComponent(referenceNumber)}`,{state: confirmationState} )} className="overflow-hidden cursor-pointer rounded-2xl border border-slate-100 bg-white shadow-md shadow-slate-200/60 transition hover:shadow-lg">
            <div className="flex items-start gap-2 p-2">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#0B67FC] to-[#062E6C] text-white shadow-md shadow-[#0B67FC]/20">
                    <StoreIcon className="h-6 w-6" />
                </span>

                <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-1">
                        <div className="min-w-0">
                            <p className="truncate text-base font-bold text-slate-900">{r.store_name || "Store"}</p>
                            <p className="truncate text-sm text-slate-500">{r.reward_name || r.description}</p>
                        </div>
                        {status && (
                            <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${STATUS_STYLES[status] || "bg-slate-100 text-slate-600"}`}>
                                {status}
                            </span>
                        )}
                    </div>

                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5" />
                            {formatDateTime(r.redemption_date)}
                        </span>
                        {r.referneceNo && <CopyRef text={r.referneceNo} />}
                    </div>
                </div>
            </div>

            {/* amounts */}
            <div className="grid grid-cols-2 divide-x divide-dashed divide-slate-200 border-t border-dashed border-slate-200 bg-slate-50/60">
                <div className="px-4 py-3">
                    <p className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Bill amount</p>
                    <p className="text-lg font-bold text-slate-800">{formatINR(r.order_amount)}</p>
                </div>
                <div className="px-4 py-3">
                    <p className="text-[11px] font-medium uppercase tracking-wider text-emerald-700">You saved</p>
                    <p className="text-lg font-extrabold text-emerald-600">{formatINR(saved)}</p>
                </div>
            </div>

            {(coins > 0 || num(r.points_deducted) > 0 || r.notes) && (
                <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 px-4 py-3">
                    {coins > 0 && (
                        <span
                            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
                                approved ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
                            }`}
                        >
                            <Coins className="h-3.5 w-3.5" />
                            {coins} GoerCoin cashback · {approved ? "Credited" : "Pending"}
                        </span>
                    )}
                    {num(r.points_deducted) > 0 && (
                        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">{r.points_deducted} points used</span>
                    )}
                    {r.notes && <span className="text-xs text-slate-500">{r.notes}</span>}
                </div>
            )}
        </div>
    );
}

export default function RedeemHistory() {
    useDocumentTitle("Redeem History");
    const dispatch = useDispatch();
    const { user } = useGoerOneAuth();
    const { items, loading, error, totalPages } = useSelector((s) => s.loyaltyRedemption);

    const [page, setPage] = useState(1);
    const [query, setQuery] = useState("");

    useEffect(() => {
        if (user?.id) dispatch(fetchLoyaltyRedemption({ perPage: PER_PAGE, page }));
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [dispatch, user?.id, page]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return [...items]
            .sort((a, b) => (parseDate(b.redemption_date)?.getTime() || 0) - (parseDate(a.redemption_date)?.getTime() || 0))
            .filter(
                (r) =>
                    !q ||
                    r.store_name?.toLowerCase().includes(q) ||
                    r.reward_name?.toLowerCase().includes(q) ||
                    r.referneceNo?.toLowerCase().includes(q)
            );
    }, [items, query]);

    // group by month
    const groups = useMemo(() => {
        const map = new Map();
        filtered.forEach((r) => {
            const d = parseDate(r.redemption_date);
            const key = d ? d.toLocaleDateString("en-IN", { month: "long", year: "numeric" }) : "Earlier";
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(r);
        });
        return [...map.entries()];
    }, [filtered]);

    const stats = useMemo(() => {
        let saved = 0;
        let approved = 0;
        let pending = 0;
        items.forEach((r) => {
            saved += num(r.discountCust);
            if (num(r.CashBack) > 0) {
                if (isCashbackApproved(r)) approved += num(r.CashBack);
                else pending += num(r.CashBack);
            }
        });
        return { saved, approved, pending };
    }, [items]);

    const showSkeleton = loading && items.length === 0;

    return (
        <div className="min-h-screen bg-[#F3F7FF]">
            <WebHeader name={user?.name || user?.customer_name || ""} />

            {/* Hero */}
            <div className="bg-gradient-to-br from-[#0B67FC] to-[#062E6C] px-4 pb-20 pt-10 text-white">
                <div className="mx-auto max-w-4xl">
                    <div className="flex items-center gap-3">
                        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15">
                            <History className="h-6 w-6" />
                        </span>
                        <div>
                            <h1 className="text-2xl font-extrabold sm:text-3xl">Redeem History</h1>
                            <p className="text-sm text-white/75">Every offer you've used and what you saved</p>
                        </div>
                    </div>

                    <div className="mt-6 grid grid-cols-3 gap-3">
                        <div className="rounded-2xl bg-white/10 px-4 py-3 backdrop-blur">
                            <p className="flex items-center gap-1.5 text-xl font-extrabold sm:text-2xl">
                                <PiggyBank className="hidden h-5 w-5 text-emerald-300 sm:block" />
                                {formatINR(stats.saved)}
                            </p>
                            <p className="text-xs font-medium uppercase tracking-wider text-white/70">Total saved</p>
                        </div>
                        <div className="rounded-2xl bg-white/10 px-4 py-3 backdrop-blur">
                            <p className="text-xl font-extrabold sm:text-2xl">{items.length}</p>
                            <p className="text-xs font-medium uppercase tracking-wider text-white/70">Redemptions</p>
                        </div>
                        <div className="rounded-2xl bg-white/10 px-4 py-3 backdrop-blur">
                            <p className="text-xl font-extrabold sm:text-2xl">{stats.approved}</p>
                            <p className="text-xs font-medium uppercase tracking-wider text-white/70">
                                Coins earned{stats.pending > 0 ? ` (+${Number(stats.pending).toFixed(2)} pending)` : ""}
                            </p>
                        </div>
                    </div>
                    {totalPages > 1 && <p className="mt-2 text-xs text-white/60">Totals are for this page of results.</p>}
                </div>
            </div>

            <div className="mx-auto -mt-12 max-w-4xl px-4 pb-16">
                <div className="rounded-2xl bg-white p-3 shadow-lg shadow-slate-200/70">
                    <div className="relative">
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search by store, offer or reference number"
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-3 text-sm outline-none transition focus:border-[#0B67FC] focus:bg-white focus:ring-2 focus:ring-[#0B67FC]/20"
                        />
                    </div>
                </div>

                {showSkeleton && (
                    <div className="mt-6 animate-pulse space-y-4">
                        {[0, 1, 2].map((i) => (
                            <div key={i} className="h-40 rounded-2xl bg-slate-200/70" />
                        ))}
                    </div>
                )}

                {!loading && error && items.length === 0 && (
                    <div className="mt-6 flex flex-col items-center rounded-2xl bg-white px-6 py-12 text-center shadow">
                        <AlertCircle className="h-10 w-10 text-rose-500" />
                        <p className="mt-3 font-semibold text-slate-800">Couldn't load your history</p>
                        <p className="mt-1 text-sm text-slate-500">{String(error)}</p>
                        <button
                            type="button"
                            onClick={() => dispatch(fetchLoyaltyRedemption({ perPage: PER_PAGE, page }))}
                            className="mt-4 flex cursor-pointer items-center gap-2 rounded-xl bg-[#0B67FC] px-5 py-2.5 text-sm font-semibold text-white"
                        >
                            <RefreshCw className="h-4 w-4" /> Try again
                        </button>
                    </div>
                )}

                {!loading && !error && items.length === 0 && (
                    <div className="mt-6 flex flex-col items-center rounded-2xl bg-white px-6 py-14 text-center shadow">
                        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#0B67FC]/10">
                            <Sparkles className="h-8 w-8 text-[#0B67FC]" />
                        </span>
                        <p className="mt-4 text-lg font-bold text-slate-800">Nothing redeemed yet</p>
                        <p className="mt-1 max-w-xs text-sm text-slate-500">Pay at a store with an offer and your savings will show up here.</p>
                        <Link to="/web/home" className="mt-5 rounded-xl bg-[#0B67FC] px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-[#0B67FC]/30">
                            Explore stores
                        </Link>
                    </div>
                )}

                {items.length > 0 && filtered.length === 0 && <p className="mt-10 text-center text-sm text-slate-500">No redemptions match your search.</p>}

                <div className={`transition-opacity ${loading && items.length > 0 ? "opacity-50" : ""}`}>
                    {groups.map(([month, rows]) => (
                        <section key={month} className="mt-6">
                            <h2 className="mb-3 px-1 text-xs font-bold uppercase tracking-widest text-slate-500">{month}</h2>
                            <div className="space-y-4">
                                {rows.map((r) => (
                                    <HistoryCard key={r.redemption_id} r={r} />
                                ))}
                            </div>
                        </section>
                    ))}
                </div>

                {totalPages > 1 && (
                    <div className="mt-8 flex items-center justify-center gap-3">
                        <button
                            type="button"
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={page <= 1 || loading}
                            className="flex cursor-pointer items-center gap-1 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <ChevronLeft className="h-4 w-4" /> Previous
                        </button>
                        <span className="text-sm font-medium text-slate-600">
                            Page {page} of {totalPages}
                        </span>
                        <button
                            type="button"
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            disabled={page >= totalPages || loading}
                            className="flex cursor-pointer items-center gap-1 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            Next <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}